import { useState } from "react";
import {
  Box,
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  CircularProgress,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Button,
  Chip,
  Stack,
  Card,
} from "@mui/material";
import { useSession } from "@/hooks/useSession";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import useDeleteData from "@/hooks/deleteHooks/useDeleteData";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import useApiErrorHandler from "@/hooks/useApiErrorHandler";
import ChecklistForm from "./checklistEditCreate";
import ChecklistDetailsPreview from "./ChecklistDetailsPreview";
import useAuthenticatedQuery from "@/hooks/useAuthenticatedQuery";
import { ArrowUpward, ArrowDownward, Sort } from "@mui/icons-material";
import { convertUTCToLocal } from "@/lib/utils/dateUtils";
import { useTranslation } from "react-i18next";
import { useTheme, alpha } from "@mui/material/styles";
import PageHeader from "@/components/PageHeader";

const ChecklistList = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const checklistsQuery = useAuthenticatedQuery(
    ["checklistsall"],
    "/api/v1/metadata/checklists?include_inactive=true",
    { staleTime: 5 * 60 * 1000 }
  );
  const { session } = useSession();

  const { data: checklists, isLoading, error, refetch } = checklistsQuery;

  const [selectedChecklistId, setSelectedChecklistId] = useState(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [editChecklist, setEditChecklist] = useState(null);
  const [nameDeleted, setNameDeleted] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const { handleApiError, setToast } = useApiErrorHandler();

  const { mutate: deleteData } = useDeleteData("/api/v1/org-admin/checklists", [
    "checklists",
  ]);

  const handleRowClick = (id) => {
    setSelectedChecklistId(id);
  };

  const handleDetailsDialogClose = () => {
    setSelectedChecklistId(null);
  };

  const handleFormDialogOpen = () => {
    setIsFormOpen(true);
  };

  const handleFormDialogClose = () => {
    setIsFormOpen(false);
    setEditChecklist(null);
  };

  const handleDeleteClick = (checklist) => {
    setItemToDelete(checklist.id);
    setNameDeleted(checklist.name);
    setDeleteModalOpen(true);
  };

  const handleEditClick = (checklist) => {
    setEditChecklist(checklist);
    setIsFormOpen(true);
  };

  const handleDeleteClose = () => {
    setItemToDelete(null);
    setDeleteModalOpen(false);
  };

  const handleConfirmDelete = async () => {
    if (itemToDelete) {
      try {
        await deleteData(itemToDelete);
        setItemToDelete(null);
        refetch();
        setToast({
          open: true,
          severity: "success",
          message: t("checklistList.deleteSuccessToast"),
        });
        setDeleteModalOpen(false);
      } catch (err) {
        handleApiError(err);
      } finally {
        setItemToDelete(null);
        setDeleteModalOpen(false);
      }
    }
  };

  const handleSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key === key && prev.direction === "asc") {
        return { key, direction: "desc" };
      }
      return { key, direction: "asc" };
    });
  };

  const sortChecklists = (checklists) => {
    if (!sortConfig.key || !checklists) return checklists;

    const sorted = [...checklists];
    const { key, direction } = sortConfig;

    sorted.sort((a, b) => {
      let aValue, bValue;

      switch (key) {
        case "name":
          aValue = a.name || t("checklistList.unknownName");
          bValue = b.name || t("checklistList.unknownName");
          break;
        case "created_at":
          aValue = a.created_at ? new Date(a.created_at).getTime() : 0;
          bValue = b.created_at ? new Date(b.created_at).getTime() : 0;
          break;
        case "updated_at":
          aValue = a.updated_at ? new Date(a.updated_at).getTime() : 0;
          bValue = b.updated_at ? new Date(b.updated_at).getTime() : 0;
          break;
        case "status":
          aValue = a.active
            ? t("checklistList.statusActive")
            : t("checklistList.statusInactive");
          bValue = b.active
            ? t("checklistList.statusActive")
            : t("checklistList.statusInactive");
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return direction === "asc" ? -1 : 1;
      if (aValue > bValue) return direction === "asc" ? 1 : -1;
      return 0;
    });

    return sorted;
  };

  const sortedChecklists = sortChecklists(checklists);

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="50vh"
      >
        <Stack alignItems="center" spacing={2}>
          <CircularProgress size={48} thickness={4} />
          <Typography variant="body1" color="text.secondary">
            {t("checklistList.loadingText")}
          </Typography>
        </Stack>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="50vh"
      >
        <Paper
          elevation={0}
          sx={{ p: 4, textAlign: "center", maxWidth: 500, borderRadius: 2 }}
        >
          <Typography color="error" variant="h6" gutterBottom>
            {t("checklistList.errorTitle")}
          </Typography>
          <Typography color="text.secondary" variant="body2">
            {t("checklistList.errorText", { error: error.message })}
          </Typography>
          <Button
            variant="outlined"
            color="primary"
            onClick={() => refetch()}
            sx={{ mt: 2 }}
          >
            {t("checklistList.retryButton")}
          </Button>
        </Paper>
      </Box>
    );
  }

  if (!sortedChecklists || sortedChecklists.length === 0) {
    return (
      <>
        <Box sx={{ p: 4, textAlign: "center" }}>
          <Card
            elevation={0}
            sx={{
              p: 6,
              borderRadius: 3,
              border: `1px dashed ${alpha(theme.palette.primary.main, 0.2)}`,
              bgcolor: alpha(theme.palette.primary.main, 0.02),
            }}
          >
            <Typography sx={{ fontSize: 48, mb: 2, opacity: 0.7 }}>
              📋
            </Typography>
            <Typography variant="h6" gutterBottom>
              {t("checklistList.noChecklistsTitle")}
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 3 }}>
              {t("checklistList.noChecklistsText")}
            </Typography>
            {session?.user_type === "ORG_ADMIN" && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleFormDialogOpen}
                sx={{
                  borderRadius: 2,
                  px: 4,
                  py: 1.5,
                  textTransform: "none",
                  fontWeight: 600,
                }}
              >
                {t("checklistList.createFirstButton")}
              </Button>
            )}
          </Card>
          <ChecklistForm
            open={isFormOpen}
            onClose={handleFormDialogClose}
            checklist={editChecklist}
            onSuccess={refetch}
          />
        </Box>
      </>
    );
  }

  const selectedChecklist = sortedChecklists.find(
    (c) => c.id === selectedChecklistId
  );

  return (
    <>
      {/* Compact Enterprise Header */}
      <PageHeader
        icon="📋"
        title={t("checklistList.title")}
        subtitle={t("checklistList.subtitle")}
        stats={[
          {
            label: `${sortedChecklists.length}`,
          },
        ]}
        actions={[
          {
            label: t("checklistList.createButton"),
            icon: <AddIcon />,
            onClick: handleFormDialogOpen,
            ariaLabel:
              t("checklistList.createButtonAria") ||
              t("checklistList.createButton"),
            show: session?.user_type === "ORG_ADMIN",
          },
        ]}
        t={t}
        translationPrefix="checklistList"
        compact={true}
      />

      {/* Table */}
      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          maxHeight: "800px",
          borderRadius: 3,
          border: `1px solid ${theme.palette.divider}`,
          "& .MuiTableCell-root": {
            borderColor: theme.palette.divider,
          },
        }}
      >
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  background: alpha(theme.palette.primary.main, 0.02),
                  fontWeight: 600,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  {t("checklistList.nameHeader")}
                  <IconButton
                    size="small"
                    onClick={() => handleSort("name")}
                    sx={{
                      ml: 1,
                      color:
                        sortConfig.key === "name"
                          ? "primary.main"
                          : "text.secondary",
                    }}
                  >
                    {sortConfig.key === "name" ? (
                      sortConfig.direction === "asc" ? (
                        <ArrowUpward fontSize="small" />
                      ) : (
                        <ArrowDownward fontSize="small" />
                      )
                    ) : (
                      <Sort fontSize="small" />
                    )}
                  </IconButton>
                </Box>
              </TableCell>
              <TableCell>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  {t("checklistList.createdAtHeader")}
                  <IconButton
                    size="small"
                    onClick={() => handleSort("created_at")}
                    sx={{
                      "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.04)" },
                    }}
                    aria-label={t("checklistList.sortCreatedAtAria")}
                  >
                    {sortConfig.key === "created_at" ? (
                      sortConfig.direction === "asc" ? (
                        <ArrowUpward fontSize="small" />
                      ) : (
                        <ArrowDownward fontSize="small" />
                      )
                    ) : (
                      <Sort fontSize="small" />
                    )}
                  </IconButton>
                </Box>
              </TableCell>
              <TableCell>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  {t("checklistList.updatedAtHeader")}
                  <IconButton
                    size="small"
                    onClick={() => handleSort("updated_at")}
                    sx={{
                      "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.04)" },
                    }}
                    aria-label={t("checklistList.sortUpdatedAtAria")}
                  >
                    {sortConfig.key === "updated_at" ? (
                      sortConfig.direction === "asc" ? (
                        <ArrowUpward fontSize="small" />
                      ) : (
                        <ArrowDownward fontSize="small" />
                      )
                    ) : (
                      <Sort fontSize="small" />
                    )}
                  </IconButton>
                </Box>
              </TableCell>
              <TableCell>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  {t("checklistList.statusHeader")}
                  <IconButton
                    size="small"
                    onClick={() => handleSort("status")}
                    sx={{
                      "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.04)" },
                    }}
                    aria-label={t("checklistList.sortStatusAria")}
                  >
                    {sortConfig.key === "status" ? (
                      sortConfig.direction === "asc" ? (
                        <ArrowUpward fontSize="small" />
                      ) : (
                        <ArrowDownward fontSize="small" />
                      )
                    ) : (
                      <Sort fontSize="small" />
                    )}
                  </IconButton>
                </Box>
              </TableCell>
              {session?.user_type === "ORG_ADMIN" && (
                <TableCell>{t("checklistList.actionHeader")}</TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedChecklists.map((checklist) => (
              <TableRow
                key={checklist.id}
                hover
                onClick={() => handleRowClick(checklist.id)}
                sx={{
                  cursor: "pointer",
                  transition: "all 0.2s",
                  "&:hover": {
                    bgcolor: alpha(theme.palette.primary.main, 0.04),
                    transform: "translateY(-1px)",
                    boxShadow: 1,
                  },
                }}
                role="button"
                aria-label={t("checklistList.rowAria", {
                  name: checklist.name || t("checklistList.unknownName"),
                })}
              >
                <TableCell>
                  <Typography fontWeight={500}>
                    {checklist.name || t("checklistList.unknownName")}
                  </Typography>
                </TableCell>
                <TableCell>
                  {convertUTCToLocal(checklist.created_at) ||
                    t("checklistList.unknownDate")}
                </TableCell>
                <TableCell>
                  {convertUTCToLocal(checklist.updated_at) ||
                    t("checklistList.unknownDate")}
                </TableCell>
                <TableCell>
                  <Chip
                    label={
                      checklist.active
                        ? t("checklistList.statusActive")
                        : t("checklistList.statusInactive")
                    }
                    color={checklist.active ? "success" : "default"}
                    size="small"
                    sx={{
                      fontWeight: 600,
                      minWidth: 80,
                      borderRadius: 1,
                    }}
                  />
                </TableCell>
                {session?.user_type === "ORG_ADMIN" && (
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <Stack direction="row" spacing={1}>
                      <IconButton
                        onClick={() => handleEditClick(checklist)}
                        size="small"
                        sx={{
                          color: "primary.main",
                          "&:hover": {
                            bgcolor: alpha(theme.palette.primary.main, 0.1),
                          },
                        }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        onClick={() => handleDeleteClick(checklist)}
                        size="small"
                        sx={{
                          color: "error.main",
                          "&:hover": {
                            bgcolor: alpha(theme.palette.error.main, 0.1),
                          },
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {selectedChecklist && (
        <Dialog
          open={!!selectedChecklistId}
          onClose={handleDetailsDialogClose}
          maxWidth="md"
          fullWidth
          aria-labelledby="checklist-details-dialog-title"
        >
          <ChecklistDetailsPreview
            checklist={selectedChecklist}
            onClose={handleDetailsDialogClose}
          />
        </Dialog>
      )}
      <ChecklistForm
        open={isFormOpen}
        onClose={handleFormDialogClose}
        checklist={editChecklist}
        onSuccess={refetch}
      />

      <Dialog
        open={deleteModalOpen}
        onClose={handleDeleteClose}
        maxWidth="sm"
        fullWidth
        sx={{
          "& .MuiDialog-paper": {
            borderRadius: 2,
            boxShadow: "0 8px 32px rgba(0,0,0,0.15)",
          },
        }}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle
          id="delete-dialog-title"
          sx={{
            background: theme.palette.checklist.surface.subtle,
            borderBottom: 1,
            borderColor: theme.palette.checklist.border.subtle,
            pb: 2,
            pt: 3,
            px: 3,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: "50%",
                bgcolor: "warning.main",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "warning.contrastText",
              }}
            >
              <CloseIcon />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: "bold", mb: 0.5 }}>
                {t("checklistList.deactivateDialogTitle")}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t("checklistList.deactivateDialogSubtitle")}
              </Typography>
            </Box>
            <IconButton
              onClick={handleDeleteClose}
              sx={{
                bgcolor: theme.palette.checklist.interactive.hover,
                "&:hover": {
                  bgcolor: theme.palette.checklist.interactive.selected,
                },
              }}
              aria-label={t("checklistList.closeDeleteDialogAria")}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent
          sx={{
            p: 3,
            bgcolor: theme.palette.checklist.surface.elevated,
          }}
          id="delete-dialog-description"
        >
          <Typography variant="body1" sx={{ mb: 2 }}>
            {t("checklistList.deactivateDialogText", {
              name: nameDeleted || t("checklistList.unknownName"),
            })}
          </Typography>
          <Box
            sx={{
              p: 2,
              bgcolor: theme.palette.checklist.surface.subtle,
              borderRadius: 1,
              border: 1,
              borderColor: "warning.light",
            }}
          >
            <Typography
              variant="body2"
              color="warning.main"
              sx={{ fontWeight: "medium" }}
            >
              ℹ️ {t("checklistList.deactivateDialogWarning")}
            </Typography>
          </Box>
        </DialogContent>

        <DialogActions
          sx={{
            p: 3,
            gap: 2,
            bgcolor: theme.palette.checklist.surface.elevated,
            borderTop: 1,
            borderColor: theme.palette.checklist.border.subtle,
          }}
        >
          <Button
            onClick={handleDeleteClose}
            variant="outlined"
            color="primary"
            sx={{
              borderRadius: 2,
              px: 3,
              py: 1,
              textTransform: "none",
              fontWeight: "medium",
            }}
            aria-label={t("checklistList.cancelDeleteButtonAria")}
          >
            {t("checklistList.cancelDeleteButton")}
          </Button>
          <Button
            onClick={handleConfirmDelete}
            variant="contained"
            color="warning"
            autoFocus
            sx={{
              borderRadius: 2,
              px: 3,
              py: 1,
              textTransform: "none",
              fontWeight: "bold",
              boxShadow: 2,
              "&:hover": {
                boxShadow: 4,
                transform: "translateY(-1px)",
              },
            }}
            aria-label={t("checklistList.confirmDeleteButtonAria")}
          >
            {t("checklistList.confirmDeleteButton")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ChecklistList;
